import { extractUniDataTableRows } from '@/common/utils/extractUniDataTableRows'

type RowLike = Record<string, unknown>

function normalizeIdentityPart(value: unknown): string {
  if (value == null) return ''
  return String(value).trim()
}

/** Стабильный ключ строки таблицы для сопоставления selection. */
export function getTableSelectionRowIdentity(row: unknown): string | undefined {
  if (!row || typeof row !== 'object') return undefined

  const source = row as RowLike
  const backend = (source._rowData ?? source._originalRow ?? source) as RowLike

  const readFieldValue = (container: RowLike | undefined, key: string): unknown => {
    if (!container) return undefined
    const fromFields = (container.fields as Record<string, { value?: unknown }> | undefined)?.[key]
    if (fromFields != null && typeof fromFields === 'object' && 'value' in fromFields) {
      return fromFields.value
    }
    return container[key]
  }

  const id =
    readFieldValue(backend, '_id') ??
    backend._id ??
    readFieldValue(source, '_id') ??
    readFieldValue(backend, 'id') ??
    readFieldValue(source, 'id') ??
    source._id ??
    source.requestId ??
    source.id

  const typeId =
    readFieldValue(backend, '_typeId') ??
    backend._typeId ??
    readFieldValue(source, '_typeId') ??
    source._typeId ??
    ''

  const versionId =
    readFieldValue(backend, '_versionId') ??
    backend._versionId ??
    readFieldValue(source, '_versionId') ??
    source._versionId ??
    ''

  const normalizedId = normalizeIdentityPart(id)
  if (normalizedId === '') return undefined

  return `${normalizedId}|${normalizeIdentityPart(typeId)}|${normalizeIdentityPart(versionId)}`
}

export function isSameTableSelectionRow(left: unknown, right: unknown): boolean {
  if (left == null || right == null) return false
  if (left === right) return true

  const leftSyntheticId = (left as RowLike).__syntheticId
  const rightSyntheticId = (right as RowLike).__syntheticId
  if (
    leftSyntheticId != null &&
    rightSyntheticId != null &&
    String(leftSyntheticId) === String(rightSyntheticId)
  ) {
    return true
  }

  const leftBackend = (left as RowLike)._rowData ?? (left as RowLike)._originalRow
  const rightBackend = (right as RowLike)._rowData ?? (right as RowLike)._originalRow

  if (leftBackend != null && rightBackend != null && leftBackend === rightBackend) {
    return true
  }
  if (leftBackend != null && leftBackend === right) return true
  if (rightBackend != null && rightBackend === left) return true

  const leftIdentity = getTableSelectionRowIdentity(left)
  const rightIdentity = getTableSelectionRowIdentity(right)
  if (leftIdentity != null && rightIdentity != null && leftIdentity === rightIdentity) {
    return true
  }

  return false
}

function resolveFallbackRowIndex(row: unknown): number {
  if (!row || typeof row !== 'object') return Number.MAX_SAFE_INTEGER
  const idx = (row as RowLike)._rowIndex
  return typeof idx === 'number' && Number.isFinite(idx) ? idx : Number.MAX_SAFE_INTEGER
}

export type OrderedCheckboxSelectionRow<T> = {
  row: T
  displayIndex: number
}

/**
 * Упорядочивает checkbox-selection по порядку строк в таблице.
 */
export function orderCheckboxSelectionRowsByDisplay<T>(
  displayItems: readonly unknown[] | null | undefined,
  selectedRows: readonly T[] | null | undefined,
): OrderedCheckboxSelectionRow<T>[] {
  if (!Array.isArray(selectedRows) || selectedRows.length === 0) {
    return []
  }

  if (!Array.isArray(displayItems) || displayItems.length === 0) {
    return [...selectedRows]
      .map((row, index) => ({ row, sourceIndex: index }))
      .sort((left, right) => resolveFallbackRowIndex(left.row) - resolveFallbackRowIndex(right.row))
      .map(({ row }, displayIndex) => ({ row, displayIndex }))
  }

  const unmatched = new Set(selectedRows.map((_, index) => index))
  const ordered: OrderedCheckboxSelectionRow<T>[] = []

  displayItems.forEach((displayItem, displayIndex) => {
    for (const selectedIndex of unmatched) {
      const selectedRow = selectedRows[selectedIndex]
      if (isSameTableSelectionRow(displayItem, selectedRow)) {
        ordered.push({ row: selectedRow, displayIndex })
        unmatched.delete(selectedIndex)
        break
      }
    }
  })

  const trailing = [...unmatched]
    .sort((left, right) => resolveFallbackRowIndex(selectedRows[left]) - resolveFallbackRowIndex(selectedRows[right]))
    .map((selectedIndex, offset) => ({
      row: selectedRows[selectedIndex],
      displayIndex: displayItems.length + offset,
    }))

  return [...ordered, ...trailing]
}

export function orderCheckboxSelectionRowsByTableField<T>(
  tableField: unknown,
  selectedRows: readonly T[] | null | undefined,
): unknown[] {
  const displayRows = extractDisplayRowsFromTableField(tableField)
  return orderCheckboxSelectionRowsByDisplay(displayRows, selectedRows).map(
    ({ row: selectedRow, displayIndex }) => displayRows[displayIndex] ?? selectedRow,
  )
}

function extractDisplayRowsFromTableField(tableField: unknown): unknown[] {
  if (tableField == null) return []

  if (Array.isArray(tableField)) {
    return tableField
  }

  if (typeof tableField !== 'object') {
    return []
  }

  const field = tableField as RowLike
  const value = 'value' in field ? field.value : field
  if (value == null) return []

  if (Array.isArray(value)) {
    return value
  }

  if (typeof value === 'object') {
    const rows = (value as RowLike).rows
    if (Array.isArray(rows)) {
      return rows
    }
  }

  return []
}

function unwrapUniDataTableFieldValue(tableField: unknown): Record<string, unknown> {
  if (tableField == null || typeof tableField !== 'object') {
    return { rows: [] }
  }

  const field = tableField as RowLike
  const rawValue = 'value' in field ? field.value : field
  if (rawValue == null) {
    return { rows: [] }
  }
  if (Array.isArray(rawValue)) {
    return { rows: rawValue }
  }
  if (typeof rawValue === 'object') {
    return { ...(rawValue as Record<string, unknown>) }
  }
  return { rows: [] }
}

/** getData для TABLE withCheckBoxes. */
export function buildCheckboxTableGetDataField(input: {
  fieldName: string
  tableField?: unknown
  selectedRows?: readonly unknown[] | null
  addAll?: boolean
}): Record<string, { type: 'UniDataTable'; value: Record<string, unknown> }> {
  const fieldName = String(input.fieldName ?? '').trim()
  if (!fieldName) return {}

  const tableValue = unwrapUniDataTableFieldValue(input.tableField)
  const allRows = extractUniDataTableRows(input.tableField)
  const rows = input.addAll
    ? allRows
    : orderCheckboxSelectionRowsByTableField(
        input.tableField ?? { type: 'UniDataTable', value: { rows: allRows } },
        input.selectedRows ?? [],
      )

  return {
    [fieldName]: {
      type: 'UniDataTable',
      value: {
        ...tableValue,
        rows,
        empty: rows.length === 0,
      },
    },
  }
}
